import { Link, useLocation } from "react-router-dom";
import { cn } from "@jambushrushti/lib/utils";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";
import {
  LayoutDashboard,
  BookOpen,
  Calendar,
  Video,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  GraduationCap,
  X,
  Users,
  ClipboardList,
  Layers,
  Users2,
  HelpCircle,
  ClipboardCheck,
  Award,
} from "lucide-react";
import { Button } from "@jambushrushti/components/ui/button";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@jambushrushti/components/ui/avatar";

// Admin navigation items
const adminNavItems = [
  {
    icon: LayoutDashboard,
    label: "Admin Dashboard",
    href: "/learning/admin-dashboard",
  },
  { icon: BookOpen, label: "Courses", href: "/learning/manage-courses" },
  { icon: Layers, label: "Programs", href: "/learning/programs" },
  { icon: Users2, label: "Batches", href: "/learning/batches" },
  { icon: Video, label: "Lectures", href: "/learning/manage-lectures" },
  { icon: HelpCircle, label: "Quizzes", href: "/learning/quizzes" },
  { icon: ClipboardCheck, label: "Assignments", href: "/learning/assignments" },
  { icon: FileText, label: "Materials", href: "/learning/materials" },
  { icon: Users, label: "Students", href: "/learning/students" },
  { icon: ClipboardList, label: "Attendance", href: "/learning/attendance" },
  { icon: Calendar, label: "Schedule", href: "/learning/schedule" },
  { icon: BarChart3, label: "Reports", href: "/learning/reports" },
  { icon: Settings, label: "Settings", href: "/learning/settings" },
];

// Student navigation item
const studentNavItem = {
  icon: GraduationCap,
  label: "My Learning",
  href: "/learning/student-dashboard",
};

interface LearningSidebarProps {
  onClose?: () => void;
}

export function LearningSidebar({ onClose }: LearningSidebarProps) {
  const location = useLocation();
  const { user, logout } = useAdminAuth();

  const handleLogout = async () => {
    await logout();
    window.location.href = "/auth";
  };

  return (
    <aside className="flex min-h-screen w-72 flex-col bg-primary text-primary-foreground lg:w-64">
      {/* Logo/Brand */}
      <div className="flex items-center justify-between border-b border-primary-foreground/10 p-4 sm:p-6">
        <Link
          to="/learning/admin-dashboard"
          className="flex items-center gap-3"
          onClick={onClose}
        >
          <div className="bg-orange/20 flex h-10 w-10 items-center justify-center rounded-xl">
            <GraduationCap className="text-orange h-6 w-6" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold text-white">
              LMS Admin
            </h1>
            <p className="text-xs text-white/60">Jambushrusti</p>
          </div>
        </Link>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="text-white/70 hover:bg-white/10 hover:text-white lg:hidden"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* User Info */}
      <div className="border-b border-primary-foreground/10 p-4">
        <div className="flex items-center gap-3">
          <Avatar className="border-orange/30 h-10 w-10 border-2">
            <AvatarImage src={user?.avatar} />
            <AvatarFallback className="bg-orange/20 text-orange font-semibold">
              {user?.name?.charAt(0).toUpperCase() || "A"}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">
              {user?.name || "Admin"}
            </p>
            <p className="truncate text-xs text-white/60">Administrator</p>
          </div>
        </div>
      </div>

      {/* Role Badge */}
      <div className="px-4 py-2">
        <div className="bg-orange/20 text-orange rounded-lg px-3 py-1.5 text-center text-xs font-medium">
          LMS Administrator
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 overflow-auto p-3 sm:p-4">
        {adminNavItems.map(item => {
          const isActive =
            location.pathname === item.href ||
            (item.href !== "/learning/admin-dashboard" &&
              location.pathname.startsWith(item.href));

          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={onClose}
              className={cn(
                "flex items-center gap-3 rounded-xl px-4 py-3 transition-all duration-200",
                isActive
                  ? "bg-orange font-medium text-white shadow-lg"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              )}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Student Dashboard Link */}
      <div className="space-y-1 border-t border-primary-foreground/10 px-4 py-2">
        <Link
          to={studentNavItem.href}
          onClick={onClose}
          className={cn(
            "flex items-center gap-3 rounded-xl px-4 py-3 transition-all duration-200",
            location.pathname === studentNavItem.href
              ? "bg-orange font-medium text-white shadow-lg"
              : "text-white/70 hover:bg-white/10 hover:text-white"
          )}
        >
          <studentNavItem.icon className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">{studentNavItem.label}</span>
        </Link>
        <Link
          to="/learning/my-courses"
          onClick={onClose}
          className={cn(
            "flex items-center gap-3 rounded-xl px-4 py-3 transition-all duration-200",
            location.pathname === "/learning/my-courses"
              ? "bg-orange font-medium text-white shadow-lg"
              : "text-white/70 hover:bg-white/10 hover:text-white"
          )}
        >
          <BookOpen className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">Enrolled Courses</span>
        </Link>
        <Link
          to="/learning/certificates"
          onClick={onClose}
          className={cn(
            "flex items-center gap-3 rounded-xl px-4 py-3 transition-all duration-200",
            location.pathname === "/learning/certificates"
              ? "bg-orange font-medium text-white shadow-lg"
              : "text-white/70 hover:bg-white/10 hover:text-white"
          )}
        >
          <Award className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">My Certificates</span>
        </Link>
      </div>

      {/* Browse All Courses */}
      <div className="border-t border-primary-foreground/10 p-4">
        <Link
          to="/learning/courses"
          onClick={onClose}
          className="flex items-center gap-3 rounded-xl px-4 py-3 text-white/70 transition-all duration-200 hover:bg-white/10 hover:text-white"
        >
          <BookOpen className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">Browse All Courses</span>
        </Link>
      </div>

      {/* Logout */}
      <div className="border-t border-primary-foreground/10 p-4">
        <Button
          variant="ghost"
          className="w-full justify-start text-white/70 hover:bg-white/10 hover:text-white"
          onClick={handleLogout}
        >
          <LogOut className="mr-3 h-5 w-5" />
          Sign Out
        </Button>
      </div>
    </aside>
  );
}
